import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["id", "className", "dataSource", "isLoading", "rowData", "totalRowData", "hasError", "emptyViewProps", "errorViewProps", "customEmptyViewComponent", "initTable", "initOnReady", "useLazyLoad", "infiniteScrolling", "useLazyWithSections", "pageSize", "infiniteInitialRowCount", "defaultColDef", "columnDefs", "useDragRows", "useDragCustom", "useDragColumns", "customDragPreview", "previewKey", "getCustomDragInfo", "canDrag", "canDrop", "onDragDrop", "saveColumnsOrder", "savePinnedColumns", "saveColumnSize", "saveVisibleColumns", "useSort", "getInitialSortInfo", "changeSortDirection", "onCancelGroupRequests", "dragGroups", "onDragEnter", "onDragEnd", "columnsRefreshOnDragEnd", "onClickRow", "fullWidthCellRenderer", "fieldSelector", "fieldSelectorProps", "activeRowId", "checkedRows", "disabledCheckboxes", "getCheckedRows", "favoriteRows", "disabledFavorites", "getFavoriteRows", "actions", "hideActions", "useFullWidthDivider", "customIsFullWidthCell", "getForceRedrawRows", "addDropZones", "showGhostWhenDrag", "onRef", "rowClass", "rowClassRules", "getTotal", "forceGetTotal", "locale", "modalOptionsProps", "useLoaderPlaceholder", "loaderPlaceholderRowHeight", "loaderPlaceholderRows", "loaderPlaceholderColumns", "useLoaderPlaceholderFullHeight", "placeholderOptions", "tooltipMenuContent", "intercomRef", "forceActionsRefresh", "getRowNodeId", "withoutOffset", "forceRowUpdating", "vanillaComponents", "components"],
  _excluded2 = ["onCancel"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useRef, createRef, useMemo, useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { createUseStyles } from 'hoi-poi-ui';
import Dinero from 'dinero.js';
import { publish } from "../../lib/EventBuser";
import { DATA_GRID_REFRESH_TOTAL } from "../../lib/events";
import { useWeb5Context } from "../../lib/Web5Context";
import TextCell from "./cells/TextCell";
import TextTwoValuesCell from "./cells/TextTwoValuesCell";
import AvatarCell from "./cells/AvatarCell";
import DateCell from "./cells/DateCell";
import CurrencyCell from "./cells/CurrencyCell";
import ValuePercentageCell from "./cells/ValuePercentageCell";
import PercentageCell from "./cells/PercentageCell";
import PercentageTextCell from "./cells/PercentageTextCell";
import BooleanCell from "./cells/BooleanCell";
import BooleanIconCell from "./cells/BooleanIconCell";
import NumberCell from "./cells/NumberCell";
import RowNumberCell from "./cells/RowNumberCell";
import ToggleCell from "./cells/ToggleCell";
import TagCell from "./cells/TagCell";
import IconCell from "./cells/IconCell";
import LoadingCell from "./cells/LoadingCell";
import MultiValueCell from "./cells/MultiValueCell";
import StepCell from "./cells/StepCell";
import FilterCell from "./cells/FilterCell";
import FullWidthDividerCell from "./cells/FullWidthDividerCell";
import TextFilter from "./filters/TextFilter";
import HeaderTextCell from "./headerCells/HeaderTextCell";
import HeaderIconCell from "./headerCells/HeaderIconCell";
import LoadingOverlay from "./LoadingOverlay";
import NoRowsOverlay from "./NoRowsOverlay";
import ToolTip from "./components/Tooltip";
import Popover from "./components/Popover";
import Actions from "./components/Actions";
import MenuOptions from "./components/MenuOptions";
import ModalOptions from "../ModalOptions";
import { addRequestToQueue, addToRowsBeforeDrag, processDragQueue } from "../../utils/requests";
import { isEqual } from "../../utils/objects";
import { getSvgSelected, getSvgNonSelected } from "../../utils/domElements";
import styles from "./styles/index";
const useStyles = createUseStyles(styles, 'DataGrid');

// Enabling input navigation
const suppressNavigation = params => {
  var _params$event$key;
  return params.event.key === 'Tab' || ((_params$event$key = params.event.key) === null || _params$event$key === void 0 ? void 0 : _params$event$key.includes('Arrow'));
};
const defaultOptions = {
  rowHeight: 50,
  headerHeight: 40,
  floatingFiltersHeight: 40,
  enableCellTextSelection: true,
  suppressRowClickSelection: true,
  suppressCellFocus: true,
  suppressMovableColumns: true,
  suppressClickEdit: true,
  suppressScrollOnNewData: true,
  suppressContextMenu: true,
  suppressDragLeaveHidesColumns: true,
  rowDataChangeDetectionStrategy: 'IdentityCheck',
  defaultColDef: {
    resizable: true,
    floatingFilter: false
  },
  columnDefs: [],
  rowData: [],
  reactNext: true,
  suppressKeyboardEvent: suppressNavigation,
  suppressTouch: true
};
var _ref10 = /*#__PURE__*/React.createElement(ToolTip, null);
var _ref11 = /*#__PURE__*/React.createElement(Popover, null);
function DataGrid(_ref) {
  let {
      id,
      className,
      dataSource,
      isLoading,
      rowData,
      totalRowData,
      hasError,
      emptyViewProps,
      errorViewProps,
      customEmptyViewComponent,
      initTable,
      initOnReady,
      useLazyLoad,
      infiniteScrolling,
      useLazyWithSections,
      pageSize,
      infiniteInitialRowCount,
      defaultColDef = {},
      columnDefs,
      useDragRows = false,
      useDragCustom = false,
      useDragColumns = false,
      customDragPreview,
      previewKey,
      getCustomDragInfo,
      canDrag,
      canDrop,
      onDragDrop,
      saveColumnsOrder,
      savePinnedColumns,
      saveColumnSize,
      saveVisibleColumns,
      useSort,
      getInitialSortInfo,
      changeSortDirection,
      onCancelGroupRequests,
      dragGroups,
      onDragEnter,
      onDragEnd,
      columnsRefreshOnDragEnd,
      onClickRow,
      fullWidthCellRenderer,
      fieldSelector,
      fieldSelectorProps,
      activeRowId,
      checkedRows,
      disabledCheckboxes,
      getCheckedRows,
      favoriteRows,
      disabledFavorites,
      getFavoriteRows,
      actions,
      hideActions,
      useFullWidthDivider,
      customIsFullWidthCell,
      getForceRedrawRows,
      addDropZones,
      showGhostWhenDrag = false,
      onRef,
      rowClass = '',
      rowClassRules = {},
      getTotal,
      forceGetTotal,
      locale,
      modalOptionsProps = {},
      useLoaderPlaceholder,
      loaderPlaceholderRowHeight,
      loaderPlaceholderRows,
      loaderPlaceholderColumns,
      useLoaderPlaceholderFullHeight,
      placeholderOptions,
      tooltipMenuContent,
      intercomRef,
      forceActionsRefresh,
      getRowNodeId,
      withoutOffset,
      forceRowUpdating,
      vanillaComponents = {},
      components = {}
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  //For lazyLoad --> Add fullWidthRows to the total, so the table will consider those rows
  // as well to the total rows to be printed.
  const getTotalWithFullWidthRows = useCallback((rowData, totalData) => {
    if (!rowData) return totalData || 0;
    if (!useFullWidthDivider) return totalData || rowData.length;
    const fullWidthRows = rowData.filter(current => current.isFullWidth);
    let totalFullWidthRows = fullWidthRows.length;
    if (totalData) return totalData + totalFullWidthRows;else return rowData.length;
  }, [useFullWidthDivider]);

  //For general use --> Removes fullWidthRows from total, so them won't be counted
  // to show the total in the header info
  const getTotalWithoutFullWidthRows = useCallback((rowData, totalData) => {
    if (!rowData) return totalData || 0;
    if (!useFullWidthDivider) return totalData || rowData.length;
    const fullWidthRows = rowData.filter(current => current.isFullWidth);
    let totalFullWidthRows = fullWidthRows.length;
    return rowData.length - totalFullWidthRows;
  }, [useFullWidthDivider]);
  const {
    t
  } = useWeb5Context();
  const classes = useStyles();
  const rootClassName = classnames(classes.root, className, 'ag-theme-balham');
  const dataGridRef = useRef(null);
  const gridRef = useRef(null);
  const gridWidthRef = useRef(0);
  const context = useWeb5Context();
  const apiRef = useRef(null);
  const isDragging = useRef(false);
  const gridApi = useRef(null);
  const gridColumnApi = useRef(null);
  const columnMovedId = useRef(null);
  const immutableRowData = useRef([]);
  const rowMoved = useRef(false);
  const immutableBeforeDrag = useRef([]);
  const isQueueProcessing = useRef(false);
  const [hoveredRowInfo, setHoveredRowInfo] = useState(null);
  const gridContext = useRef({
    tableId: id || '',
    tableRef: /*#__PURE__*/createRef(),
    totalData: totalRowData || getTotalWithoutFullWidthRows(rowData) || 0,
    checkboxData: checkedRows || null,
    favoriteData: favoriteRows || null,
    getHoveredRowInfo: setHoveredRowInfo,
    hasActions: actions && (actions === null || actions === void 0 ? void 0 : actions.length) > 0,
    tableData: rowData || [],
    tableDataForLazySections: [],
    updateTableData: data => gridContext.current.tableData = data,
    useDragCustom,
    canDrag,
    t,
    hasShowTotal: false,
    locale: locale || ''
  });
  const oldInitialActiveRowId = useRef(null);
  const sortInfoForLazyLoad = useRef(null);
  const [suppressNoRowsOverlay, setSuppressNoRowsOverlay] = useState(true);
  const [hiddenColumns, setHiddenColumns] = useState(null);
  const [isLazyLoaded, setIsLazyLoaded] = useState(false);
  const defaultColumnMinWidth = 100;
  const lazyLoadInfo = useRef(null);
  const [mappedSchema, setMappedSchema] = useState([]);
  const [selectedFields, setSelectedFields] = useState({});
  const isFirstLoad = useRef(true);
  const oldSelectedFields = useRef({});
  const dragObserver = useRef();
  const dragTarget = useRef();

  //TODO set the currencyISO depending on the locale
  const setupCurrencyLocaleUser = useCallback(function (locale, currencyISO, currencySymbol) {
    if (currencyISO === void 0) {
      currencyISO = '';
    }
    if (currencySymbol === void 0) {
      currencySymbol = '';
    }
    Dinero.globalLocale = locale;
    Dinero.defaultCurrency = currencyISO;
    Dinero.defaultUserCurrencySymbol = currencySymbol;
  }, []);

  // We need immutable data for row drag and drop actions
  useEffect(() => {
    if ((rowData === null || rowData === void 0 ? void 0 : rowData.length) > 0) {
      immutableRowData.current = [...rowData];
      gridContext.current.tableData = rowData;
    }
    if (!gridContext.current.hasShowTotal && !forceGetTotal && !totalRowData) return;
    const previousTotal = gridContext.current.totalData;
    const currentTotal = getTotalWithoutFullWidthRows(rowData) || 0;
    if (previousTotal !== currentTotal) {
      gridContext.current.totalData = currentTotal;
      publish(`${DATA_GRID_REFRESH_TOTAL}-${id}`, currentTotal);
    }
  }, [rowData, getTotalWithoutFullWidthRows, id, forceGetTotal, totalRowData]);

  // Get hidden columns
  useEffect(() => {
    if (fieldSelector) {
      if (columnDefs && columnDefs.length > 0) {
        const finalHiddenColumns = columnDefs.reduce((obj, column) => {
          if (!column.suppressFromFieldSelector) {
            obj[column.colId] = {
              name: column.headerName,
              hide: !!column.hide
            };
          }
          return obj;
        }, {});
        setHiddenColumns(finalHiddenColumns);
      }
    }
  }, [columnDefs, fieldSelector]);
  const shouldHideGhostRows = useCallback(hide => {
    var _gridApi$current$grid;
    if (!gridApi.current) return;
    const bodyViewport = (_gridApi$current$grid = gridApi.current.gridBodyCtrl) === null || _gridApi$current$grid === void 0 ? void 0 : _gridApi$current$grid.eBodyViewport;
    if (bodyViewport) bodyViewport.style.opacity = hide ? 0 : 'inherit';
  }, []);

  // Refresh the cache to reaload the data in lazyLoad table (useful for example after CRUD actions).
  const refresh = useCallback(lazyInfo => {
    var _lazyLoadInfo$current, _gridApi$current2, _gridApi$current3;
    lazyLoadInfo.current = lazyInfo;
    if (lazyLoadInfo !== null && lazyLoadInfo !== void 0 && (_lazyLoadInfo$current = lazyLoadInfo.current) !== null && _lazyLoadInfo$current !== void 0 && _lazyLoadInfo$current.resetOffset) {
      var _gridApi$current;
      gridApi === null || gridApi === void 0 ? void 0 : (_gridApi$current = gridApi.current) === null || _gridApi$current === void 0 ? void 0 : _gridApi$current.ensureIndexVisible(0, 'top');
    }

    // purgeInfiniteCache clears the table cache and causes a dataSource getRows execution getting new data
    gridApi === null || gridApi === void 0 ? void 0 : (_gridApi$current2 = gridApi.current) === null || _gridApi$current2 === void 0 ? void 0 : _gridApi$current2.purgeInfiniteCache();
    shouldHideGhostRows(true);

    // When there are no rows and the noRowsOverlay is being shown and we retrieve new data, in orther to
    // >> prevent a flickering and remove the noRowsOverlay, we execute hideOverlay;
    gridApi === null || gridApi === void 0 ? void 0 : (_gridApi$current3 = gridApi.current) === null || _gridApi$current3 === void 0 ? void 0 : _gridApi$current3.hideOverlay();
  }, [shouldHideGhostRows]);
  const refreshCheckboxes = useCallback((colId, areAllChecked) => {
    const tableId = id;
    const tableElement = document.getElementById(tableId);
    const checkboxCells = tableElement.querySelectorAll(`[col-id='${colId}']`);
    checkboxCells.forEach(cell => {
      var _disabledCheckboxes$c;
      if (cell.classList.contains('w5-header')) return;
      const cellWrapper = cell.querySelector('.w5-cell');
      if (!cellWrapper) return;
      const rowId = cellWrapper.getAttribute('data-rowid');
      const cellCheckboxContainer = cellWrapper.querySelector('.w5-cell-checkbox');
      if (!cellCheckboxContainer) return;
      const isVisible = cellCheckboxContainer.classList.contains('w5-cell-checkbox--visible');
      const isHidden = cellCheckboxContainer.classList.contains('w5-cell-checkbox--hidden');
      const isBlocked = cellCheckboxContainer.classList.contains('w5-cell-checkbox--blocked');
      const isDisabled = cellCheckboxContainer.classList.contains('w5-cell-checkbox--disabled');
      if (isVisible || isHidden) {
        if (areAllChecked) {
          cellCheckboxContainer.classList.remove('w5-cell-checkbox--hidden');
          cellCheckboxContainer.classList.add('w5-cell-checkbox--visible');
        } else {
          cellCheckboxContainer.classList.remove('w5-cell-checkbox--visible');
          cellCheckboxContainer.classList.add('w5-cell-checkbox--hidden');
        }
      }
      const svgContainer = cellWrapper.querySelector('.w5-cell-checkbox__svg-container');
      if (!svgContainer || disabledCheckboxes !== null && disabledCheckboxes !== void 0 && (_disabledCheckboxes$c = disabledCheckboxes[colId]) !== null && _disabledCheckboxes$c !== void 0 && _disabledCheckboxes$c[rowId] || isDisabled) return;
      if (areAllChecked) {
        if (isBlocked) return;
        const svgSelected = getSvgSelected(tableId, colId, rowId);
        if (!svgSelected) return;
        svgContainer.innerHTML = svgSelected;
        cellCheckboxContainer.setAttribute('data-ischecked', true);
      } else {
        const svgNonSelected = getSvgNonSelected(tableId, colId, rowId);
        if (!svgNonSelected) return;
        svgContainer.innerHTML = svgNonSelected;
        cellCheckboxContainer.setAttribute('data-ischecked', false);
      }
    });
  }, [disabledCheckboxes, id]);
  const clearCheckboxes = useCallback(() => {
    var _gridContext$current, _gridApi$current4;
    if (!(gridContext !== null && gridContext !== void 0 && (_gridContext$current = gridContext.current) !== null && _gridContext$current !== void 0 && _gridContext$current.checkboxData)) return;
    const newCheckboxData = Object.keys(gridContext.current.checkboxData).reduce((obj, current) => {
      refreshCheckboxes(current, false);
      obj[current] = {
        rows: {},
        areAllChecked: false,
        lazyAreAllChecked: false
      };
      return obj;
    }, {});
    gridContext.current.checkboxData = newCheckboxData;
    (_gridApi$current4 = gridApi.current) === null || _gridApi$current4 === void 0 ? void 0 : _gridApi$current4.refreshHeader();
  }, [refreshCheckboxes]);
  const newGetCheckedRows = useCallback(() => {
    var _gridContext$current2, _gridApi$current5, _gridApi$current6;
    if (!getCheckedRows) return;
    const newChecked = getCheckedRows(immutableRowData.current);
    const checkboxData = (gridContext === null || gridContext === void 0 ? void 0 : (_gridContext$current2 = gridContext.current) === null || _gridContext$current2 === void 0 ? void 0 : _gridContext$current2.checkboxData) || {};
    if (!checkboxData || !newChecked || Object.keys(newChecked).length === 0 || isEqual(checkboxData, newChecked) || !gridColumnApi.current) {
      return;
    }
    let columns = Object.keys(newChecked).reduce((arr, current) => {
      const oldColumn = gridColumnApi.current.getColumn(current);
      if (oldColumn) arr.push(oldColumn);
      return arr;
    }, []);
    const finalChecked = checkboxData;
    Object.keys(newChecked).forEach(column => {
      if (!finalChecked[column]) finalChecked[column] = newChecked[column];else {
        finalChecked[column].areAllChecked = newChecked[column].areAllChecked;
        finalChecked[column].lazyAreAllChecked = newChecked[column].lazyAreAllChecked;
        if (!finalChecked[column].rows) newChecked[column].rows;else {
          finalChecked[column].rows = _objectSpread(_objectSpread({}, finalChecked[column].rows), newChecked[column].rows);
        }
      }
    });
    gridContext.current.checkboxData = finalChecked;
    (_gridApi$current5 = gridApi.current) === null || _gridApi$current5 === void 0 ? void 0 : _gridApi$current5.refreshCells({
      columns,
      force: true
    });
    (_gridApi$current6 = gridApi.current) === null || _gridApi$current6 === void 0 ? void 0 : _gridApi$current6.refreshHeader();
  }, [getCheckedRows]);
  const newGetFavoriteRows = useCallback(rowData => {
    var _gridContext$current3;
    if (!getFavoriteRows) return;
    let newFavorite;
    if (rowData) {
      newFavorite = getFavoriteRows(rowData);
    } else newFavorite = getFavoriteRows(immutableRowData.current);
    const data = (gridContext === null || gridContext === void 0 ? void 0 : (_gridContext$current3 = gridContext.current) === null || _gridContext$current3 === void 0 ? void 0 : _gridContext$current3.favoriteData) || {};
    if (!data || !newFavorite || Object.keys(newFavorite).length === 0 || isEqual(data, newFavorite)) return;
    let columns;
    if (gridColumnApi.current) {
      var _Object$keys;
      columns = (_Object$keys = Object.keys(newFavorite)) === null || _Object$keys === void 0 ? void 0 : _Object$keys.reduce((arr, current) => {
        const oldColumn = gridColumnApi.current.getColumn(current);
        if (oldColumn) arr.push(oldColumn);
        return arr;
      }, []);
    }
    const finalFavorite = data;
    Object.keys(newFavorite).forEach(column => {
      if (!finalFavorite[column]) finalFavorite[column] = newFavorite[column];else {
        if (!finalFavorite[column].rows) newFavorite[column].rows;else {
          finalFavorite[column].rows = _objectSpread(_objectSpread({}, finalFavorite[column].rows), newFavorite[column].rows);
        }
      }
    });
    gridContext.current.favoriteData = finalFavorite;
    if (columns) gridApi.current.refreshCells({
      columns,
      force: true
    });
  }, [getFavoriteRows]);
  const updateRow = useCallback((id, fields) => {
    const rowNode = gridApi.current.getRowNode(String(id));
    if (!rowNode) return;
    let data = rowNode.data;
    Object.keys(fields).forEach(current => {
      data[current] = fields[current];
    });
    gridApi.current.redrawRows({
      rowNodes: [rowNode]
    });
  }, []);
  const refreshColumnByColId = useCallback(colIds => {
    var _gridApi$current7;
    if ((colIds === null || colIds === void 0 ? void 0 : colIds.length) === 0) return;
    let columns = colIds.reduce((arr, current) => {
      var _gridColumnApi$curren, _gridColumnApi$curren2;
      const column = gridColumnApi === null || gridColumnApi === void 0 ? void 0 : (_gridColumnApi$curren = gridColumnApi.current) === null || _gridColumnApi$curren === void 0 ? void 0 : (_gridColumnApi$curren2 = _gridColumnApi$curren.getColumn) === null || _gridColumnApi$curren2 === void 0 ? void 0 : _gridColumnApi$curren2.call(_gridColumnApi$curren, current);
      if (column) arr.push(column);
      return arr;
    }, []);
    (_gridApi$current7 = gridApi.current) === null || _gridApi$current7 === void 0 ? void 0 : _gridApi$current7.refreshCells({
      columns,
      force: true
    });
  }, []);

  // Child method
  useEffect(() => {
    onRef && onRef({
      refresh,
      clearCheckboxes,
      updateRow,
      updateFavoriteRow: newGetFavoriteRows,
      getApi: () => gridApi.current,
      refreshColumnByColId
    });
  }, [onRef, refresh, clearCheckboxes, updateRow, newGetFavoriteRows, refreshColumnByColId]);

  //Updates favoriteRows and drag rows when receiving new rowData when the table is !useLazyLoad
  useEffect(() => {
    if (!rowData || (rowData === null || rowData === void 0 ? void 0 : rowData.length) === 0) return;
    if (useLazyLoad) return;
    newGetFavoriteRows(rowData);
  }, [newGetFavoriteRows, rowData, useLazyLoad]);

  // Control the overlays to display
  useEffect(() => {
    // Sometimes this code is executed before gridApi exists so let's wait a tick
    setTimeout(() => {
      if (!(gridApi !== null && gridApi !== void 0 && gridApi.current)) return;
      if (isLoading) {
        var _gridApi$current8, _gridApi$current8$sho;
        setSuppressNoRowsOverlay(true);
        gridApi === null || gridApi === void 0 ? void 0 : (_gridApi$current8 = gridApi.current) === null || _gridApi$current8 === void 0 ? void 0 : (_gridApi$current8$sho = _gridApi$current8.showLoadingOverlay) === null || _gridApi$current8$sho === void 0 ? void 0 : _gridApi$current8$sho.call(_gridApi$current8);
      } else {
        var _gridApi$current9, _gridApi$current9$siz;
        gridApi === null || gridApi === void 0 ? void 0 : (_gridApi$current9 = gridApi.current) === null || _gridApi$current9 === void 0 ? void 0 : (_gridApi$current9$siz = _gridApi$current9.sizeColumnsToFit) === null || _gridApi$current9$siz === void 0 ? void 0 : _gridApi$current9$siz.call(_gridApi$current9);
        if (!useLazyLoad) {
          if ((rowData === null || rowData === void 0 ? void 0 : rowData.length) > 0) {
            gridApi.current.hideOverlay();
          } else {
            setSuppressNoRowsOverlay(false);
            gridApi.current.showNoRowsOverlay();
          }
        } else if (isLazyLoaded) {
          var _immutableRowData$cur;
          if (immutableRowData.current && (immutableRowData === null || immutableRowData === void 0 ? void 0 : (_immutableRowData$cur = immutableRowData.current) === null || _immutableRowData$cur === void 0 ? void 0 : _immutableRowData$cur.length) > 0) {
            gridApi.current.hideOverlay();
          } else {
            setSuppressNoRowsOverlay(false);
            gridApi.current.showNoRowsOverlay();
          }
        }
      }
    });

    // Fix for dataGrid glitch that was showing an empty row before
    // loading the custom LoadingOverlay
    if (gridContext.current.totalData === 0 && useLazyLoad) {
      var _dataGridRef$current, _dataGridRef$current$;
      (_dataGridRef$current = dataGridRef.current) === null || _dataGridRef$current === void 0 ? void 0 : (_dataGridRef$current$ = _dataGridRef$current.classList) === null || _dataGridRef$current$ === void 0 ? void 0 : _dataGridRef$current$.add(classes.isEmpty);
    } else {
      var _dataGridRef$current2, _dataGridRef$current3;
      (_dataGridRef$current2 = dataGridRef.current) === null || _dataGridRef$current2 === void 0 ? void 0 : (_dataGridRef$current3 = _dataGridRef$current2.classList) === null || _dataGridRef$current3 === void 0 ? void 0 : _dataGridRef$current3.remove(classes.isEmpty);
    }
  }, [useLazyLoad, rowData, isLoading, gridApi, suppressNoRowsOverlay, isLazyLoaded, classes.isLoading, classes.isEmpty]);
  const setRowSelected = useCallback(activeRowId => {
    if (!(gridApi !== null && gridApi !== void 0 && gridApi.current)) return;
    gridApi.current.deselectAll();
    const activeNode = gridApi.current.getRowNode(String(activeRowId));
    if (activeNode) activeNode.setSelected(true);
  }, []);
  useEffect(() => {
    setRowSelected(activeRowId);
  }, [activeRowId, setRowSelected]);
  setupCurrencyLocaleUser(context.locale);

  // This informs us about the current sort direction that the header column is displaying
  // >> so we can retrieve the correct sort to backend
  const getSortInfoForLazyLoad = useCallback(sortInfo => {
    sortInfoForLazyLoad.current = sortInfo;
  }, []);

  //For lazyLoad we define the dataSource here
  const setGridDataSource = useCallback(() => {
    shouldHideGhostRows(true);
    const dataSource = {
      getRows: params => {
        var _lazyLoadInfo$current2;
        let sortInfo = null;
        // Fast loading overlay
        // We show the loading overlay because when we execute the initTable,
        // >> the table suddenly has no data and thinks that is because has no result.
        // So it shows the noRowsOverlay, while we are retrieving the sorted data from backend.
        // This happens because the getRows has been triggered by the setSortModel and not because the scroll
        // >> so the table thinks that has to sort the data, but we do it through backend instead
        // As soon as has data again, the table itself will remove the showLoadingOverlay.
        if (!params.startRow) {
          var _dataGridRef$current4, _dataGridRef$current5;
          (_dataGridRef$current4 = dataGridRef.current) === null || _dataGridRef$current4 === void 0 ? void 0 : (_dataGridRef$current5 = _dataGridRef$current4.classList) === null || _dataGridRef$current5 === void 0 ? void 0 : _dataGridRef$current5.add(classes.isLoading);
          shouldHideGhostRows(true);
        }
        gridContext.current.startRow = params.startRow;
        gridApi.current.showLoadingOverlay();
        if (sortInfoForLazyLoad.current) {
          sortInfo = {
            sortField: sortInfoForLazyLoad.current.sortField || sortInfoForLazyLoad.current.colId,
            sortDir: sortInfoForLazyLoad.current.sort
          };
        }
        setIsLazyLoaded(false);
        const previousTotal = gridContext.current.totalData;
        let startRow = 0;
        if (lazyLoadInfo !== null && lazyLoadInfo !== void 0 && (_lazyLoadInfo$current2 = lazyLoadInfo.current) !== null && _lazyLoadInfo$current2 !== void 0 && _lazyLoadInfo$current2.resetOffset) {
          startRow = 0;
          lazyLoadInfo.current = null;
        } else startRow = params.startRow;
        function innerGetTotal() {
          // Only first time

          if (getTotal && startRow === 0 && (gridContext.current.hasShowTotal || forceGetTotal)) {
            return getTotal().then(total => {
              gridContext.current.totalData = total;
            }).catch(() => {
              gridContext.current.totalData = 0;
            }).then(() => {
              publish(`${DATA_GRID_REFRESH_TOTAL}-${id}`, gridContext.current.totalData);
            });
          } else return Promise.resolve();
        }
        Promise.all([innerGetTotal(), initTable(pageSize, startRow, sortInfo)]).then(_ref2 => {
          let [_, result] = _ref2;
          const {
            entities,
            total,
            lastRow
          } = result;

          // Because "total" is only real in the first call. After that it's always 0.
          if (!getTotal) {
            if (entities.length > 0 && total) {
              gridContext.current.totalData = total;
            } else if (!(entities !== null && entities !== void 0 && entities.length)) gridContext.current.totalData = 0;
          }

          // so we keep immutableRowData updated
          immutableRowData.current = [...entities];

          // When adding sections with fullWidthRow, the table doesn't control well the total rows to print,
          // >> so we control it using the grigContext.current.table data to force to not print more rows
          // >> than necessary in the last pagination
          if (useLazyWithSections) {
            if (startRow === 0) gridContext.current.tableDataForLazySections = [];
            const isLastPage = entities.length < pageSize;
            if (isLastPage) {
              params.successCallback(entities, gridContext.current.tableDataForLazySections.length + entities.length);
            } else {
              params.successCallback(entities);
            }
            gridContext.current.tableDataForLazySections = [...gridContext.current.tableDataForLazySections, ...entities];
          } else if (infiniteScrolling) {
            params.successCallback(entities, lastRow);
          } else {
            // Second param is lastRow and it should be provided when we know it
            params.successCallback(entities, !getTotal || entities.length < pageSize || pageSize === gridContext.current.totalData ? getTotalWithFullWidthRows(entities, gridContext.current.totalData) : undefined);
          }
        }).catch(error => {
          if (error) console.error(error);
          immutableRowData.current = [];
          gridContext.current.totalData = 0;
          params.failCallback();
        }).then(() => {
          var _dataGridRef$current6, _dataGridRef$current7;
          // Finally callback
          if (getForceRedrawRows !== null && getForceRedrawRows !== void 0 && getForceRedrawRows()) {
            var _gridApi$current10;
            // This method is to force the table to ignore/forget cache memory in the rendering so,
            // >> we ignore the result of the purgeInfiniteCache that ignored the redraw of several
            // >> rows besides those were updated, but didn't reflected the change on the table
            // This is very specific for the case where we have a table with fullWidthRows and useLazyLoad (inifinite scroll)
            // >> and we need to refresh the table without a mounting the component again or populating it with a complete different data.
            // In short. Without this, the table makes a refresh, but the fullWidthRows aren't displayed as fullWidthRows but normal rows.
            gridApi === null || gridApi === void 0 ? void 0 : (_gridApi$current10 = gridApi.current) === null || _gridApi$current10 === void 0 ? void 0 : _gridApi$current10.redrawRows();
          }
          shouldHideGhostRows(false);
          (_dataGridRef$current6 = dataGridRef.current) === null || _dataGridRef$current6 === void 0 ? void 0 : (_dataGridRef$current7 = _dataGridRef$current6.classList) === null || _dataGridRef$current7 === void 0 ? void 0 : _dataGridRef$current7.remove(classes.isLoading);
          setIsLazyLoaded(true);
          newGetFavoriteRows();
          newGetCheckedRows();
          if (!getTotal && (gridContext.current.hasShowTotal || forceGetTotal) && previousTotal !== gridContext.current.totalData && startRow === 0) {
            publish(`${DATA_GRID_REFRESH_TOTAL}-${id}`, gridContext.current.totalData);
          }
        });
      }
    };
    gridApi.current.setDatasource(dataSource);
  }, [shouldHideGhostRows, initTable, pageSize, classes.isLoading, getTotal, id, useLazyWithSections, infiniteScrolling, getTotalWithFullWidthRows, getForceRedrawRows, newGetFavoriteRows, newGetCheckedRows, forceGetTotal]);
  const onReady = useCallback(params => {
    if (initOnReady) {
      if (useLazyLoad) {
        setGridDataSource();
      } else initTable(pageSize);
    }
  }, [initOnReady, useLazyLoad, initTable, setGridDataSource, pageSize]);

  // Main events
  const onGridReady = useCallback(params => {
    gridRef.current = params;
    gridApi.current = params.api;
    gridColumnApi.current = params.columnApi;
    addDropZones && addDropZones(params);
    onReady && onReady(params);
    if (useDragCustom) {
      var _dragObserver$current;
      (_dragObserver$current = dragObserver.current) === null || _dragObserver$current === void 0 ? void 0 : _dragObserver$current.disconnect();
      dragObserver.current = addCustomDrag({
        getCustomDragInfo,
        immutableRowData: immutableRowData,
        rowData,
        dragPreview: customDragPreview,
        previewKey,
        canDrop,
        onDragDrop,
        dragTarget,
        canDrag
      });
    }
  }, [addDropZones, onReady, useDragCustom, getCustomDragInfo, rowData, customDragPreview, previewKey, canDrop, onDragDrop, canDrag]);
  const onGridSizeChanged = useCallback(params => {
    const clientWidth = gridWidthRef.current;
    gridWidthRef.current = params.clientWidth;
    if (params.clientWidth && clientWidth && params.clientwidth === clientWidth) return;
    try {
      setTimeout(() => {
        var _params$api, _params$api$sizeColum;
        params === null || params === void 0 ? void 0 : (_params$api = params.api) === null || _params$api === void 0 ? void 0 : (_params$api$sizeColum = _params$api.sizeColumnsToFit) === null || _params$api$sizeColum === void 0 ? void 0 : _params$api$sizeColum.call(_params$api);
      });
    } catch (e) {
      console.error(e);
    }
  }, []);
  const onFirstDataRendered = useCallback(params => {
    try {
      var _params$api2, _params$api2$sizeColu;
      params === null || params === void 0 ? void 0 : (_params$api2 = params.api) === null || _params$api2 === void 0 ? void 0 : (_params$api2$sizeColu = _params$api2.sizeColumnsToFit) === null || _params$api2$sizeColu === void 0 ? void 0 : _params$api2$sizeColu.call(_params$api2);
      apiRef.current = params.api;
    } catch (e) {
      console.error(e);
    }
  }, []);
  const moveInArray = useCallback((arr, fromIndex, toIndex) => {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }, []);
  const onRowDragEnter = useCallback(event => {
    if (showGhostWhenDrag) {
      const ghostRow = document.querySelector('.ag-dnd-ghost');
      if (ghostRow) ghostRow.classList.add('ag-dnd-ghost--visible');
    }
    isDragging.current = true;
    setDraggedRow(event.api, event.node, true);
    removeChildrenOnDrag(event.api, onCancelGroupRequests);
    immutableBeforeDrag.current = [];
    event.api.forEachNode(current => {
      immutableBeforeDrag.current.push(current.data);
    });
    addToRowsBeforeDrag(immutableBeforeDrag.current);
    onDragEnter && onDragEnter(event);
  }, [isDragging, onCancelGroupRequests, immutableBeforeDrag, onDragEnter, showGhostWhenDrag]);
  const onRowDragMove = useCallback(event => {
    const movingNode = event.node;
    const overNode = event.overNode;
    const rowNeedsToMove = movingNode !== overNode;
    if (rowNeedsToMove) {
      const movingData = movingNode === null || movingNode === void 0 ? void 0 : movingNode.data;
      const overData = overNode === null || overNode === void 0 ? void 0 : overNode.data;
      if (!overData) return;
      if (dragGroups !== null && dragGroups !== void 0 && dragGroups.groups && dragGroups !== null && dragGroups !== void 0 && dragGroups.groupsKey) {
        const movingDataKey = movingData[dragGroups.groupsKey];
        const group = dragGroups === null || dragGroups === void 0 ? void 0 : dragGroups.groups[movingDataKey];
        if (movingDataKey && group && !group.includes(overData.id)) {
          return;
        }
      }
      const offset = gridApi.current.getFirstDisplayedRow();
      const fromIndex = immutableRowData.current.indexOf(movingData);
      let toIndex = immutableRowData.current.indexOf(overData) + offset;
      if (withoutOffset) toIndex = immutableRowData.current.indexOf(overData);
      const newRowData = immutableRowData.current.slice();
      moveInArray(newRowData, fromIndex, toIndex);
      immutableRowData.current = newRowData;
      rowMoved.current = true;
      gridContext.current.updateTableData(newRowData);
      gridApi.current.setRowData(newRowData);
      gridApi.current.clearFocusedCell();
    }
  }, [dragGroups, moveInArray, withoutOffset]);
  const onRowDragEnd = useCallback(event => {
    if (rowMoved.current && onDragEnd) {
      const data = {
        rowDataBefore: immutableBeforeDrag.current
      };
      addRequestToQueue(() => onDragEnd(event, data));
      if (!isQueueProcessing.current) {
        isQueueProcessing.current = true;
        processDragQueue(event, immutableRowData, isQueueProcessing);
      }
    }
    setDraggedRow(event.api, event.node);
    rowMoved.current = false;
    if (columnsRefreshOnDragEnd) {
      let columns = columnsRefreshOnDragEnd.filter(current => {
        return gridColumnApi.current.getColumn(current);
      });
      gridApi.current.refreshCells({
        columns,
        force: true
      });
    }
  }, [onDragEnd, columnsRefreshOnDragEnd]);
  const onRowDragLeave = useCallback(event => {
    isDragging.current = false;
    setDraggedRow(event.api, event.node);
  }, []);
  const onColumnMoved = useCallback(event => {
    var _event$column;
    if (!(event !== null && event !== void 0 && (_event$column = event.column) !== null && _event$column !== void 0 && _event$column.colId)) return;
    columnMovedId.current = event.column.colId;
  }, []);
  const onColumnPinned = useCallback(event => {
    savePinnedColumns && savePinnedColumns(event.column);
  }, [savePinnedColumns]);
  const onColumnResized = useCallback(event => {
    if (saveColumnSize && event.finished) {
      var _event$columns;
      (_event$columns = event.columns) === null || _event$columns === void 0 ? void 0 : _event$columns.forEach(column => {
        var _gridApi$current11, _gridApi$current11$si;
        if ((column === null || column === void 0 ? void 0 : column.colId) === 'fmFieldSelectorSpacer') return;
        saveColumnSize(column.colId, column.actualWidth);
        (_gridApi$current11 = gridApi.current) === null || _gridApi$current11 === void 0 ? void 0 : (_gridApi$current11$si = _gridApi$current11.sizeColumnsToFit) === null || _gridApi$current11$si === void 0 ? void 0 : _gridApi$current11$si.call(_gridApi$current11);
      });
    }
  }, [saveColumnSize]);
  const onRowSelected = useCallback(event => {
    var _event$node, _event$node$isSelecte;
    if (event !== null && event !== void 0 && (_event$node = event.node) !== null && _event$node !== void 0 && (_event$node$isSelecte = _event$node.isSelected) !== null && _event$node$isSelecte !== void 0 && _event$node$isSelecte.call(_event$node)) {
      oldInitialActiveRowId.current = event.node.id;
    }
  }, []);
  const onViewportChanged = useCallback(event => {
    // To be executed when the table has to load with a row selected
    if (activeRowId && (!(oldInitialActiveRowId !== null && oldInitialActiveRowId !== void 0 && oldInitialActiveRowId.current) || oldInitialActiveRowId.current !== activeRowId)) {
      setRowSelected(activeRowId);
    }
  }, [activeRowId, setRowSelected]);
  const onRowClicked = useCallback(event => {
    if (isDragging.current) {
      isDragging.current = false;
      return;
    }
    onClickRow && onClickRow(event);
  }, [onClickRow]);
  const getIsAllChecked = useCallback((context, colId) => {
    var _context$checkboxData, _context$checkboxData2;
    const checkedInColumn = (context === null || context === void 0 ? void 0 : (_context$checkboxData = context.checkboxData) === null || _context$checkboxData === void 0 ? void 0 : (_context$checkboxData2 = _context$checkboxData[colId]) === null || _context$checkboxData2 === void 0 ? void 0 : _context$checkboxData2.rows) || null;
    const checkedList = checkedInColumn && Object.keys(checkedInColumn).filter(current => {
      return checkedInColumn[current];
    });

    // checkedList contains all the rows checked of the loaded rows
    if (checkedList.length === context.totalData) {
      context.checkboxData[colId].areAllChecked = true;
    } else context.checkboxData[colId].areAllChecked = false;
  }, []);
  const getIsRowChecked = useCallback((colId, rowId) => {
    let isChecked = false;
    const context = gridContext.current;
    if (useLazyLoad) {
      var _context$checkboxData3, _context$checkboxData4;
      if (context.checkboxData[colId].rows[rowId]) {
        isChecked = context.checkboxData[colId].rows[rowId];
      } else if (typeof ((_context$checkboxData3 = context.checkboxData[colId].rows) === null || _context$checkboxData3 === void 0 ? void 0 : _context$checkboxData3[rowId]) === 'boolean' && !((_context$checkboxData4 = context.checkboxData[colId].rows) !== null && _context$checkboxData4 !== void 0 && _context$checkboxData4[rowId])) {
        isChecked = false;
      } else if (context.checkboxData[colId].lazyAreAllChecked) {
        isChecked = true;
      } else isChecked = false;
    } else {
      isChecked = context.checkboxData[colId].rows[rowId] || false;
    }
    return isChecked;
  }, [useLazyLoad]);

  // Here we build the resultant checkboxData after checkbox click
  const onClickCheckbox = useCallback(checkboxCallback => {
    return _ref3 => {
      var _context$checkboxData5, _context$checkboxData6, _gridApi$current12;
      let {
        id,
        colId,
        boolean,
        context,
        column,
        rowIndex,
        data
      } = _ref3;
      if (column && !colId) return;else if (!colId && !id) return;
      let totalBlocked = 0;
      if (!(context !== null && context !== void 0 && context.checkboxData)) context.checkboxData = {};
      if (!(context !== null && context !== void 0 && (_context$checkboxData5 = context.checkboxData) !== null && _context$checkboxData5 !== void 0 && _context$checkboxData5[colId])) {
        context.checkboxData[colId] = {};
        context.checkboxData[colId].rows = {};
      }
      if (column) {
        let newCheckboxes = {};
        gridApi.current.forEachNode(node => {
          var _disabledCheckboxes$c2, _node$data, _column$colDef$cellRe, _column$colDef$cellRe2, _column$colDef$cellRe3, _column$colDef$cellRe4, _column$colDef$cellRe5, _column$colDef$cellRe6;
          if (disabledCheckboxes !== null && disabledCheckboxes !== void 0 && (_disabledCheckboxes$c2 = disabledCheckboxes[colId]) !== null && _disabledCheckboxes$c2 !== void 0 && _disabledCheckboxes$c2[node.id]) return;
          if ((node === null || node === void 0 ? void 0 : (_node$data = node.data) === null || _node$data === void 0 ? void 0 : _node$data.oldSelected) === '1') {
            newCheckboxes[node.id] = true;
            return;
          }
          if ((_column$colDef$cellRe = column.colDef.cellRendererParams) !== null && _column$colDef$cellRe !== void 0 && (_column$colDef$cellRe2 = _column$colDef$cellRe.checkbox) !== null && _column$colDef$cellRe2 !== void 0 && _column$colDef$cellRe2.isDisabled && (_column$colDef$cellRe3 = column.colDef.cellRendererParams) !== null && _column$colDef$cellRe3 !== void 0 && (_column$colDef$cellRe4 = _column$colDef$cellRe3.checkbox) !== null && _column$colDef$cellRe4 !== void 0 && _column$colDef$cellRe4.isDisabled(colId, id, node) && (_column$colDef$cellRe5 = column.colDef.cellRendererParams) !== null && _column$colDef$cellRe5 !== void 0 && (_column$colDef$cellRe6 = _column$colDef$cellRe5.checkbox) !== null && _column$colDef$cellRe6 !== void 0 && _column$colDef$cellRe6.noCheckedWhenDisabled) {
            totalBlocked++;
            return;
          }
          newCheckboxes[node.id] = boolean;
        });
        context.checkboxData[colId].areAllChecked = boolean;
        context.checkboxData[colId].rows = newCheckboxes;
        if (useLazyLoad) {
          context.checkboxData[colId].lazyAreAllChecked = boolean;
        }
      } else {
        context.checkboxData[colId].rows[id] = boolean;
        if (useLazyLoad) {
          getIsAllChecked(context, colId);
          context.checkboxData[colId].lazyAreAllChecked = context.checkboxData[colId].lazyAreAllChecked || false;
        } else if (!boolean) {
          context.checkboxData[colId].areAllChecked = false;
        } else {
          getIsAllChecked(context, colId);
        }
      }
      const areAllChecked = (context === null || context === void 0 ? void 0 : (_context$checkboxData6 = context.checkboxData[colId]) === null || _context$checkboxData6 === void 0 ? void 0 : _context$checkboxData6.areAllChecked) || null;
      checkboxCallback && checkboxCallback({
        id: id || null,
        colId,
        isColumn: !!column,
        value: boolean,
        checked: context.checkboxData,
        rowIndex,
        data,
        total: areAllChecked ? context.totalData - totalBlocked : 0
      });
      if (column) {
        refreshCheckboxes(colId, areAllChecked);
      } else (_gridApi$current12 = gridApi.current) === null || _gridApi$current12 === void 0 ? void 0 : _gridApi$current12.refreshHeader();
    };
  }, [useLazyLoad, disabledCheckboxes, getIsAllChecked, refreshCheckboxes]);
  const getIsRowFavorite = useCallback((colId, rowId) => {
    let isFavorite = false;
    const context = gridContext.current;
    if (useLazyLoad) {
      var _context$favoriteData, _context$favoriteData2;
      if (context.favoriteData[colId].rows[rowId]) {
        isFavorite = context.favoriteData[colId].rows[rowId];
      } else if (typeof ((_context$favoriteData = context.favoriteData[colId].rows) === null || _context$favoriteData === void 0 ? void 0 : _context$favoriteData[rowId]) === 'boolean' && !((_context$favoriteData2 = context.favoriteData[colId].rows) !== null && _context$favoriteData2 !== void 0 && _context$favoriteData2[rowId])) {
        isFavorite = false;
      } else isFavorite = false;
    } else {
      isFavorite = context.favoriteData[colId].rows[rowId] || false;
    }
    return isFavorite;
  }, [useLazyLoad]);
  const onClickFavorite = useCallback(favoriteCallback => {
    return _ref4 => {
      var _context$favoriteData3;
      let {
        id,
        colId,
        boolean,
        context,
        rowIndex,
        data
      } = _ref4;
      if (!colId && !id) return;
      if (!(context !== null && context !== void 0 && context.favoriteData)) context.favoriteData = {};
      if (!(context !== null && context !== void 0 && (_context$favoriteData3 = context.favoriteData) !== null && _context$favoriteData3 !== void 0 && _context$favoriteData3[colId])) {
        context.favoriteData[colId] = {};
        context.favoriteData[colId].rows = {};
      }
      context.favoriteData[colId].rows[id] = boolean;
      favoriteCallback && favoriteCallback({
        id: id || null,
        colId,
        value: boolean,
        checked: context.favoriteData,
        rowIndex,
        data
      });
    };
  }, []);
  const newFullWidthCellRender = useMemo(() => {
    if (useFullWidthDivider) return FullWidthDividerCell;else {
      return fullWidthCellRenderer && !onCancelGroupRequests ? fullWidthCellRenderer : LoadingCell;
    }
  }, [fullWidthCellRenderer, onCancelGroupRequests, useFullWidthDivider]);
  const loadingIsFullWidthCell = useCallback(rowNode => {
    var _rowNode$data;
    return (rowNode === null || rowNode === void 0 ? void 0 : (_rowNode$data = rowNode.data) === null || _rowNode$data === void 0 ? void 0 : _rowNode$data.isFullWidth) || false;
  }, []);
  const isFullWidthRow = useCallback(_ref5 => {
    let {
      rowNode
    } = _ref5;
    if (customIsFullWidthCell) return customIsFullWidthCell(rowNode) || loadingIsFullWidthCell(rowNode);else return loadingIsFullWidthCell(rowNode);
  }, [customIsFullWidthCell, loadingIsFullWidthCell]);
  const finalRowHeight = props.rowHeight || defaultOptions.rowHeight;
  const getRowHeight = useCallback(params => {
    var _params$data;
    return params !== null && params !== void 0 && (_params$data = params.data) !== null && _params$data !== void 0 && _params$data.isLoader ? 150 : finalRowHeight;
  }, [finalRowHeight]);
  const customRowCells = useMemo(() => ({
    textCell: TextCell,
    textTwoValuesCell: TextTwoValuesCell,
    avatarCell: AvatarCell,
    dateCell: DateCell,
    currencyCell: CurrencyCell,
    valuePercentageCell: ValuePercentageCell,
    percentageCell: PercentageCell,
    percentageTextCell: PercentageTextCell,
    booleanCell: BooleanCell,
    booleanIconCell: BooleanIconCell,
    numberCell: NumberCell,
    rowNumberCell: RowNumberCell,
    toggleCell: ToggleCell,
    tagCell: TagCell,
    iconCell: IconCell,
    multiValueCell: MultiValueCell,
    stepCell: StepCell,
    filterCell: FilterCell,
    fullWidthCellRenderer: newFullWidthCellRender,
    textFilter: TextFilter,
    headerTextCell: HeaderTextCell,
    headerIconCell: HeaderIconCell
  }), [newFullWidthCellRender]);
  const wrappedChangeSortDirection = useCallback(props => {
    changeSortDirection && changeSortDirection(props);
  }, [changeSortDirection]);
  const newChangeSortDirection = useCallback(getSortInfoForLazyLoad => {
    return sortModel => {
      if (getSortInfoForLazyLoad) getSortInfoForLazyLoad(sortModel);
    };
  }, []);
  const finalColumnDefs = useMemo(() => columnDefs.map((current, idx) => {
    var _current$headerCompon, _current$headerCompon2, _current$headerCompon3, _current$cellRenderer, _current$cellRenderer2, _current$cellRenderer3, _current$cellRenderer4, _gridContext$current4, _gridContext$current5;
    // If we have actions but the current column doesn't use a customCellRenderer, we set one
    // because the actions are controlled from the Base cell in the customCells
    if ((actions === null || actions === void 0 ? void 0 : actions.length) > 0 && (!current.cellRenderer || !customRowCells[current.cellRenderer] && !components[current.cellRenderer])) {
      current.cellRenderer = 'textCell';
    }
    if (!current.cellRendererParams) current.cellRendererParams = {};
    current.cellRendererParams = _objectSpread(_objectSpread({}, current.cellRendererParams), context);
    if (!current.headerComponentParams) current.headerComponentParams = {};
    if (useDragColumns && current.rowDrag) {
      current.lockPosition = true;
      current.suppressMovable = true;
    } else if (useDragColumns && !current.lockPosition && !current.rowDrag) {
      if (!current.headerComponent) current.headerComponent = 'headerTextCell';
      if (!current.suppressMovable) {
        current.headerComponentParams = _objectSpread(_objectSpread({}, current.headerComponentParams), {}, {
          useDragColumns: true
        });
      }
    }
    if (useDragRows) {
      current.headerComponentParams = _objectSpread(_objectSpread({}, current.headerComponentParams), {}, {
        useDragRows: true
      });
    }
    if (useSort) {
      if (!current.headerComponent) current.headerComponent = 'headerTextCell';
      current.sortable = current.hasOwnProperty('sortable') ? current.sortable : true;
    }
    if (useSort && changeSortDirection && current.sortField) {
      current.headerComponentParams.sortField = current.sortField;
      current.headerComponentParams.changeSortDirection = wrappedChangeSortDirection;
    }
    if (useSort) {
      if (getInitialSortInfo) {
        const initialSort = getInitialSortInfo();
        if (current.colId === (initialSort === null || initialSort === void 0 ? void 0 : initialSort.sortField)) current.sort = initialSort.sortDirection;
        current.headerComponentParams.getInitialSortInfo = getInitialSortInfo;
      }
      if (useLazyLoad) {
        current.headerComponentParams.getSortInfoForLazyLoad = newChangeSortDirection(getSortInfoForLazyLoad);
      }
    }
    if (!current.minWidth) {
      current.minWidth = (current === null || current === void 0 ? void 0 : current.width) < defaultColumnMinWidth ? current.width : defaultColumnMinWidth;
    }
    if (current !== null && current !== void 0 && (_current$headerCompon = current.headerComponentParams) !== null && _current$headerCompon !== void 0 && _current$headerCompon.showTotal) {
      gridContext.current.hasShowTotal = true;
    }
    if (current !== null && current !== void 0 && (_current$headerCompon2 = current.headerComponentParams) !== null && _current$headerCompon2 !== void 0 && (_current$headerCompon3 = _current$headerCompon2.checkbox) !== null && _current$headerCompon3 !== void 0 && _current$headerCompon3.onChange) {
      const onChange = current.headerComponentParams.checkbox.onChange;
      delete current.headerComponentParams.checkbox.onChange;
      current.headerComponentParams.checkbox.onClick = onClickCheckbox(onChange);
    }
    if (current !== null && current !== void 0 && (_current$cellRenderer = current.cellRendererParams) !== null && _current$cellRenderer !== void 0 && (_current$cellRenderer2 = _current$cellRenderer.checkbox) !== null && _current$cellRenderer2 !== void 0 && _current$cellRenderer2.onChange) {
      const onChange = current.cellRendererParams.checkbox.onChange;
      delete current.cellRendererParams.checkbox.onChange;
      current.cellRendererParams.checkbox.onClick = onClickCheckbox(onChange);
      current.cellRendererParams.checkbox.getIsRowChecked = getIsRowChecked;
    }
    if (current !== null && current !== void 0 && (_current$cellRenderer3 = current.cellRendererParams) !== null && _current$cellRenderer3 !== void 0 && (_current$cellRenderer4 = _current$cellRenderer3.favorite) !== null && _current$cellRenderer4 !== void 0 && _current$cellRenderer4.onChange) {
      const onChange = current.cellRendererParams.favorite.onChange;
      delete current.cellRendererParams.favorite.onChange;
      current.cellRendererParams.favorite.onClick = onClickFavorite(onChange);
      current.cellRendererParams.favorite.getIsRowFavorite = getIsRowFavorite;
    }
    current.headerClass = current.headerClass ? `${current.headerClass} w5-header` : `w5-header`;
    if (current.headerComponent) {
      current.headerClass = `${current.headerClass} w5-header-custom-cell`;
    }
    const align = current.cellRendererParams.align;
    const alignHeader = current.cellRendererParams.alignHeader;
    if (alignHeader) current.headerClass = `${current.headerClass} w5-header--${alignHeader}`;else if (align) current.headerClass = `${current.headerClass} w5-header--${align}`;
    const removeHover = current.headerComponentParams.removeHover;
    if (!removeHover) current.headerClass = `${current.headerClass} w5-header--hover`;
    current.cellClassRules = _objectSpread({
      'w5-cell-container': function (params) {
        return true;
      },
      // Under this class we can edit native classes of the
      // >> table without breaking the styles of the native cellRenderer
      'w5-cell-container-custom-cell': function (params) {
        if (current.cellRenderer) return true;else return false;
      },
      'w5-cell-container-drag': function (params) {
        return useDragRows;
      },
      'w5-cell-container-no-drag': function (params) {
        return !useDragRows;
      },
      'w5-cell-container--dragging': function (params) {
        if (draggedRow) {
          var _draggedRow, _draggedRow$data, _params$data2;
          return ((_draggedRow = draggedRow) === null || _draggedRow === void 0 ? void 0 : (_draggedRow$data = _draggedRow.data) === null || _draggedRow$data === void 0 ? void 0 : _draggedRow$data.id) && (params === null || params === void 0 ? void 0 : (_params$data2 = params.data) === null || _params$data2 === void 0 ? void 0 : _params$data2.id) && draggedRow.data.id === params.data.id;
        } else return false;
      },
      'w5-cell-container-lazy-drag-custom': function (params) {
        return useDragCustom;
      },
      'w5-cell-container--no-border-left': function (params) {
        var _params$colDef, _params$colDef$cellRe;
        return (params === null || params === void 0 ? void 0 : (_params$colDef = params.colDef) === null || _params$colDef === void 0 ? void 0 : (_params$colDef$cellRe = _params$colDef.cellRendererParams) === null || _params$colDef$cellRe === void 0 ? void 0 : _params$colDef$cellRe.isLast) || false;
      },
      'w5-cell-container--no-border-right': function (params) {
        var _params$colDef2, _params$colDef2$cellR;
        return (params === null || params === void 0 ? void 0 : (_params$colDef2 = params.colDef) === null || _params$colDef2 === void 0 ? void 0 : (_params$colDef2$cellR = _params$colDef2.cellRendererParams) === null || _params$colDef2$cellR === void 0 ? void 0 : _params$colDef2$cellR.isFirst) || false;
      },
      'w5-cell-container--no-border-left-and-right': function (params) {
        var _params$colDef3, _params$colDef3$cellR, _params$colDef4, _params$colDef4$cellR;
        if (!(params !== null && params !== void 0 && (_params$colDef3 = params.colDef) !== null && _params$colDef3 !== void 0 && (_params$colDef3$cellR = _params$colDef3.cellRendererParams) !== null && _params$colDef3$cellR !== void 0 && _params$colDef3$cellR.isFirst) && !(params !== null && params !== void 0 && (_params$colDef4 = params.colDef) !== null && _params$colDef4 !== void 0 && (_params$colDef4$cellR = _params$colDef4.cellRendererParams) !== null && _params$colDef4$cellR !== void 0 && _params$colDef4$cellR.isLast) && params.colDef.cellRenderer === 'stepCell') return true;else return false;
      },
      'w5-first-cell-row-selectable': function (params) {
        var _params$colDef5, _params$colDef6, _params$node, _params$node$columnCo, _params$node$columnCo2;
        if (params !== null && params !== void 0 && (_params$colDef5 = params.colDef) !== null && _params$colDef5 !== void 0 && _params$colDef5.colId && (params === null || params === void 0 ? void 0 : (_params$colDef6 = params.colDef) === null || _params$colDef6 === void 0 ? void 0 : _params$colDef6.colId) === (params === null || params === void 0 ? void 0 : (_params$node = params.node) === null || _params$node === void 0 ? void 0 : (_params$node$columnCo = _params$node.columnController) === null || _params$node$columnCo === void 0 ? void 0 : (_params$node$columnCo2 = _params$node$columnCo.columnDefs) === null || _params$node$columnCo2 === void 0 ? void 0 : _params$node$columnCo2[0].colId)) {
          return true;
        } else return false;
      }
    }, current.cellClassRules);

    // Limiting the width to avoid that a column cannot be resized later.
    if (current.pinned && (_gridContext$current4 = gridContext.current) !== null && _gridContext$current4 !== void 0 && (_gridContext$current5 = _gridContext$current4.tableRef.current) !== null && _gridContext$current5 !== void 0 && _gridContext$current5.eGridDiv) {
      var _gridContext$current6, _gridContext$current7, _gridContext$current8, _gridContext$current9;
      const offsetWidth = (gridContext === null || gridContext === void 0 ? void 0 : (_gridContext$current6 = gridContext.current) === null || _gridContext$current6 === void 0 ? void 0 : (_gridContext$current7 = _gridContext$current6.tableRef) === null || _gridContext$current7 === void 0 ? void 0 : (_gridContext$current8 = _gridContext$current7.current) === null || _gridContext$current8 === void 0 ? void 0 : (_gridContext$current9 = _gridContext$current8.eGridDiv) === null || _gridContext$current9 === void 0 ? void 0 : _gridContext$current9.offsetWidth) || 100;
      const limit = offsetWidth * 0.75;
      if (current.width > limit) current.width = limit;
    }

    // Removing duplicates
    const classSet = new Set(current.headerClass.split(' '));
    current.headerClass = Array.from(classSet).join(' ');

    // Fixing last column width when actions set
    if ((columnDefs === null || columnDefs === void 0 ? void 0 : columnDefs.length) === idx + 1 && actions !== null && actions !== void 0 && actions.length) {
      current.suppressSizeToFit = false;
      current.resizable = false;
    }
    return current;
  }), [actions, changeSortDirection, columnDefs, components, context, customRowCells, getInitialSortInfo, getIsRowChecked, getIsRowFavorite, getSortInfoForLazyLoad, wrappedChangeSortDirection, newChangeSortDirection, onClickCheckbox, onClickFavorite, useDragColumns, useDragRows, useDragCustom, useLazyLoad, useSort]);
  const onDragStopped = useCallback(event => {
    if (saveColumnsOrder && columnMovedId.current) {
      const columnOrder = gridColumnApi.current.getAllDisplayedColumns().map(column => {
        return column.getColId();
      });
      const indexOfMoved = columnOrder.indexOf(columnMovedId.current);
      const columnAfter = columnOrder[indexOfMoved - 1] || null;
      const reorderedColumn = columnOrder[indexOfMoved];
      saveColumnsOrder(columnAfter, reorderedColumn);
      if (actions !== null && actions !== void 0 && actions.length) {
        // fix last column props
        const columns = gridApi.current.getColumnDefs();
        columns.forEach((column, idx) => {
          if (idx + 1 === (columns === null || columns === void 0 ? void 0 : columns.length)) {
            column.resizable = false;
            column.suppressSizeToFit = false;
            column.width = undefined;
          } else {
            const originalColumn = columnDefs.find(def => def.colId === column.colId);
            column.suppressSizeToFit = true;
            column.resizable = originalColumn.hasOwnProperty('resizable') && reorderedColumn !== column.colId ? originalColumn.resizable : true;
          }
        });
        gridApi.current.setColumnDefs(columns);
      }
    }
  }, [actions === null || actions === void 0 ? void 0 : actions.length, columnDefs, saveColumnsOrder]);
  const onRowDataUpdated = useCallback(() => {
    if (forceRowUpdating) {
      var _gridApi$current13;
      (_gridApi$current13 = gridApi.current) === null || _gridApi$current13 === void 0 ? void 0 : _gridApi$current13.refreshCells({
        force: true
      });
    }
  }, [forceRowUpdating]);
  const options = useMemo(() => {
    let newColumnDefs = finalColumnDefs;
    let affectedByDrag = {
      suppressRowDrag: true,
      getRowId: getRowNodeId ? _ref6 => {
        let {
          data
        } = _ref6;
        return getRowNodeId(data);
      } : _ref7 => {
        let {
          data
        } = _ref7;
        return data === null || data === void 0 ? void 0 : data.id;
      }
    };
    if (useDragRows) {
      if (!affectedByDrag.getRowId) {
        affectedByDrag.getRowId = function (data) {
          return data.id;
        };
      }
      affectedByDrag.animateRows = true;
      affectedByDrag.suppressRowDrag = false;
      affectedByDrag.rowDragManaged = false;
    }
    if (fieldSelector) {
      // Fake column for fieldSelector space
      // This avoids the last column to be hidden below the 3 dots menu in the top right of the table
      newColumnDefs = [...finalColumnDefs, {
        colId: 'fmFieldSelectorSpacer',
        minWidth: 44,
        maxWidth: 44,
        resizable: false,
        headerClass: 'w5-header w5-header__field-selector',
        cellClass: 'w5-cell__field-selector'
      }];
    }
    const newOptions = _objectSpread(_objectSpread(_objectSpread({}, defaultOptions), {}, {
      rowData: !useLazyLoad ? rowData : null,
      rowSelection: 'single',
      rowModelType: useLazyLoad ? 'infinite' : null,
      cacheBlockSize: useLazyLoad ? pageSize : null,
      rowBuffer: 0,
      blockLoadDebounceMillis: 300,
      maxConcurrentDatasourceRequests: 3,
      layoutInterval: 200,
      tooltipShowDelay: 200,
      components: _objectSpread(_objectSpread(_objectSpread({}, customRowCells), vanillaComponents), {}, {
        loadingOverlay: function () {
          for (var _len = arguments.length, props = new Array(_len), _key = 0; _key < _len; _key++) {
            props[_key] = arguments[_key];
          }
          return /*#__PURE__*/React.createElement(LoadingOverlay, _extends({}, props, {
            id: id,
            rowData: immutableRowData.current,
            useLoaderPlaceholder: useLoaderPlaceholder,
            placeholderOptions: placeholderOptions,
            loaderRowHeight: loaderPlaceholderRowHeight || finalRowHeight,
            loaderRows: loaderPlaceholderRows,
            loaderColumns: loaderPlaceholderColumns,
            useLoaderFullHeight: useLoaderPlaceholderFullHeight,
            gridContext: gridContext
          }));
        },
        noRowsOverlay: customEmptyViewComponent || NoRowsOverlay
      }, components),
      suppressNoRowsOverlay,
      loadingOverlayComponent: 'loadingOverlay',
      noRowsOverlayComponent: 'noRowsOverlay',
      noRowsOverlayComponentParams: {
        hasError,
        isLoading,
        emptyViewProps,
        errorViewProps,
        headerHeight: 40
      },
      defaultColDef: _objectSpread(_objectSpread({}, defaultOptions.defaultColDef), defaultColDef),
      columnDefs: newColumnDefs,
      fullWidthCellRenderer: 'fullWidthCellRenderer',
      isFullWidthRow,
      getRowHeight,
      rowClass: ['w5-row', rowClass, onClickRow && 'w5-row--click'].join(' '),
      rowClassRules: _objectSpread({
        'w5-row-children': function (params) {
          var _params$data3;
          return params === null || params === void 0 ? void 0 : (_params$data3 = params.data) === null || _params$data3 === void 0 ? void 0 : _params$data3.isChildren;
        },
        'w5-row-loader': function (params) {
          var _params$data4;
          return params === null || params === void 0 ? void 0 : (_params$data4 = params.data) === null || _params$data4 === void 0 ? void 0 : _params$data4.isLoader;
        },
        'w5-row-group--open': function (params) {
          var _params$data5, _params$data6;
          if (params !== null && params !== void 0 && (_params$data5 = params.data) !== null && _params$data5 !== void 0 && _params$data5.isGroup && params !== null && params !== void 0 && (_params$data6 = params.data) !== null && _params$data6 !== void 0 && _params$data6.isGroupOpen) {
            return true;
          }
        },
        'w5-row-group--close': function (params) {
          var _params$data7, _params$data8;
          if (params !== null && params !== void 0 && (_params$data7 = params.data) !== null && _params$data7 !== void 0 && _params$data7.isGroup && !(params !== null && params !== void 0 && (_params$data8 = params.data) !== null && _params$data8 !== void 0 && _params$data8.isGroupOpen)) {
            return true;
          }
        },
        'w5-row-group--open__no-events': function (params) {
          var _params$data9, _params$data10, _params$data11;
          if (params !== null && params !== void 0 && (_params$data9 = params.data) !== null && _params$data9 !== void 0 && _params$data9.isGroup && params !== null && params !== void 0 && (_params$data10 = params.data) !== null && _params$data10 !== void 0 && _params$data10.isGroupOpen && params !== null && params !== void 0 && (_params$data11 = params.data) !== null && _params$data11 !== void 0 && _params$data11.isLoading) {
            return true;
          }
        },
        'w5-row-full-width': function (params) {
          return isFullWidthRow({
            rowNode: params.node
          }) || false;
        }
      }, rowClassRules),
      onRowDragEnter,
      onRowDragMove,
      onRowDragEnd,
      onRowDragLeave,
      onRowClicked,
      onDragStopped,
      onColumnMoved,
      onColumnPinned,
      onColumnResized,
      onViewportChanged,
      onRowSelected,
      getRowId: getRowNodeId ? _ref8 => {
        let {
          data
        } = _ref8;
        return getRowNodeId(data);
      } : null,
      onRowDataUpdated
    }, props), affectedByDrag);
    if (dataSource) {
      newOptions.rowModelType = 'infinite';
      newOptions.datasource = dataSource;
    }
    if (useDragColumns) {
      newOptions.suppressMovableColumns = false;
    }
    return newOptions;
  }, [finalColumnDefs, props, useDragRows, fieldSelector, useLazyLoad, rowData, pageSize, customRowCells, vanillaComponents, customEmptyViewComponent, components, suppressNoRowsOverlay, hasError, isLoading, emptyViewProps, errorViewProps, defaultColDef, isFullWidthRow, getRowHeight, rowClass, onClickRow, rowClassRules, onRowDragEnter, onRowDragMove, onRowDragEnd, onRowDragLeave, onRowClicked, onDragStopped, onColumnMoved, onColumnPinned, onColumnResized, onViewportChanged, onRowSelected, getRowNodeId, onRowDataUpdated, dataSource, useDragColumns, id, useLoaderPlaceholder, placeholderOptions, loaderPlaceholderRowHeight, finalRowHeight, loaderPlaceholderRows, loaderPlaceholderColumns, useLoaderPlaceholderFullHeight]);
  useEffect(() => {
    if (gridRef && gridRef.current && gridRef.current.api && gridRef.current.api.setInfiniteRowCount && infiniteInitialRowCount) {
      gridRef.current.api.setInfiniteRowCount(infiniteInitialRowCount);
    }
    return () => {
      var _dragObserver$current2;
      return (_dragObserver$current2 = dragObserver.current) === null || _dragObserver$current2 === void 0 ? void 0 : _dragObserver$current2.disconnect();
    };
  }, [infiniteInitialRowCount]);
  useEffect(() => {
    return () => {
      onCancelGroupRequests && onCancelGroupRequests();
    };
  }, [onCancelGroupRequests]);

  // Convert the grid columns schema to the format expected by ModalOptions
  const getModalOptionsSchema = useCallback(groupedColumns => {
    let newMappedSchema = [];
    if ((groupedColumns === null || groupedColumns === void 0 ? void 0 : groupedColumns.length) > 0) {
      newMappedSchema = groupedColumns.map(item => {
        const options = item.fields.filter(option => !option.suppressFromFieldSelector).map(option => ({
          id: option.colId,
          label: option.headerName
        }));
        return {
          label: item.label || t('label_fields'),
          id: item.label || t('label_fields'),
          options: options
        };
      });
    }
    setMappedSchema([...newMappedSchema]);
  }, [t]);
  const onChangeSelectedFields = useCallback(newSelectedFields => {
    setSelectedFields(_objectSpread({}, newSelectedFields));
  }, []);
  const getSelectedFields = useCallback((mappedSchema, hiddenColumns) => {
    const newSelectedFields = mappedSchema.reduce((obj, group) => {
      const options = group.options.reduce((fields, field) => {
        if (hiddenColumns[field.id] && !hiddenColumns[field.id].hide) fields.push(field.id);
        return fields;
      }, []);
      obj[group.id] = {
        isSelected: options.length > 0 && options.length === group.options.length || false,
        options
      };
      return obj;
    }, {});
    oldSelectedFields.current = JSON.parse(JSON.stringify(newSelectedFields));
    onChangeSelectedFields(newSelectedFields);
  }, [onChangeSelectedFields]);
  const {
      onCancel: handleCloseModalOptions
    } = modalOptionsProps,
    otherModalOptionsProps = _objectWithoutProperties(modalOptionsProps, _excluded2);
  const handleCancelExportModal = useCallback(() => {
    handleCloseModalOptions && handleCloseModalOptions(false);
    setSelectedFields(JSON.parse(JSON.stringify(oldSelectedFields.current)));
  }, [handleCloseModalOptions]);
  const handleConfirmExportModal = useCallback(selectedFields => {
    if (Object.keys(selectedFields).length > 0 && mappedSchema.length > 0) {
      oldSelectedFields.current = JSON.parse(JSON.stringify(selectedFields));
      const selectedColumns = Object.values(selectedFields).reduce((arr, _ref9) => {
        let {
          options
        } = _ref9;
        return [...arr, ...options];
      }, []);
      const newSelectedFields = mappedSchema.map(optionGroup => optionGroup.options).flat().reduce((obj, item) => _objectSpread(_objectSpread({}, obj), {}, {
        [item.id]: selectedColumns.includes(item.id)
      }), {});
      const newHiddenColumns = Object.entries(newSelectedFields).reduce((fields, field) => {
        const [column, visible] = field;
        gridColumnApi.current.setColumnVisible(column, visible);
        fields[column] = _objectSpread(_objectSpread({}, hiddenColumns[column]), {}, {
          hide: !visible
        });
        return fields;
      }, {});
      saveVisibleColumns(newSelectedFields);
      setHiddenColumns(newHiddenColumns);
    }
    handleCloseModalOptions(false);
  }, [handleCloseModalOptions, hiddenColumns, mappedSchema, saveVisibleColumns]);
  useEffect(() => {
    var _fieldSelectorProps$g;
    if (isFirstLoad.current && (fieldSelectorProps === null || fieldSelectorProps === void 0 ? void 0 : (_fieldSelectorProps$g = fieldSelectorProps.groupedColumns) === null || _fieldSelectorProps$g === void 0 ? void 0 : _fieldSelectorProps$g.length) > 0) {
      getModalOptionsSchema(fieldSelectorProps.groupedColumns);
      isFirstLoad.current = false;
    }
    if (hiddenColumns && Object.keys(hiddenColumns).length > 0) {
      getSelectedFields(mappedSchema, hiddenColumns);
    }
  }, [fieldSelectorProps, getModalOptionsSchema, getSelectedFields, hiddenColumns, mappedSchema]);
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: rootClassName,
    ref: dataGridRef
  }, actions && (actions === null || actions === void 0 ? void 0 : actions.length) > 0 && /*#__PURE__*/React.createElement(Actions, {
    actions: actions,
    hideActions: hideActions,
    hoveredRowInfo: hoveredRowInfo,
    id: id,
    dataGridRef: dataGridRef.current,
    rowData: rowData,
    isLazyLoaded: isLazyLoaded,
    intercomRef: intercomRef,
    isLoading: isLoading,
    forceActionsRefresh: forceActionsRefresh
  }), fieldSelector && /*#__PURE__*/React.createElement(MenuOptions, {
    tooltipMenuContent: tooltipMenuContent,
    fieldSelector: fieldSelector,
    fieldSelectorProps: fieldSelectorProps
  }), _ref10, _ref11, /*#__PURE__*/React.createElement(AgGridReact, _extends({
    ref: gridContext.current.tableRef,
    onGridReady: onGridReady,
    onGridSizeChanged: onGridSizeChanged,
    onFirstDataRendered: onFirstDataRendered,
    infiniteInitialRowCount: infiniteInitialRowCount,
    isLoading: isLoading,
    context: gridContext.current
  }, options)), Object.entries(otherModalOptionsProps).length > 0 && /*#__PURE__*/React.createElement(ModalOptions, _extends({
    schema: mappedSchema,
    selected: selectedFields,
    onCancel: handleCancelExportModal,
    onChangeSelected: onChangeSelectedFields,
    onConfirm: handleConfirmExportModal
  }, otherModalOptionsProps)));
}
process.env.NODE_ENV !== "production" ? DataGrid.propTypes = {
  className: PropTypes.string
} : void 0;
export default /*#__PURE__*/React.memo(DataGrid);
let draggedRow = null;
function setDraggedRow(api, rowNode, isDragging) {
  draggedRow = isDragging && rowNode || null;
  let rowsToRefresh = [rowNode];
  refreshDraggedRow(api, rowsToRefresh);
}
function refreshDraggedRow(api, rowsToRefresh) {
  const params = {
    rowNodes: rowsToRefresh,
    force: true
  };
  api.refreshCells(params);
}
function removeChildrenOnDrag(api, onCancelGroupRequests) {
  onCancelGroupRequests && onCancelGroupRequests();
  api.forEachNode(current => {
    if (onCancelGroupRequests) {
      var _current$data, _current$data2;
      if (current !== null && current !== void 0 && (_current$data = current.data) !== null && _current$data !== void 0 && _current$data.isLoader) {
        api.applyTransaction({
          remove: [current.data]
        });
      } else if (current !== null && current !== void 0 && (_current$data2 = current.data) !== null && _current$data2 !== void 0 && _current$data2.isGroupOpen) {
        var _current$data3, _current$data3$childr;
        //can be open because isLoading but not having children yet
        delete current.data.isGroupOpen;
        if ((current === null || current === void 0 ? void 0 : (_current$data3 = current.data) === null || _current$data3 === void 0 ? void 0 : (_current$data3$childr = _current$data3.children) === null || _current$data3$childr === void 0 ? void 0 : _current$data3$childr.length) > 0) {
          api.applyTransaction({
            remove: current.data.children
          });
          delete current.data.children;
        }
        api.applyTransaction({
          update: [current.data]
        });
      }
    } else {
      var _current$data4, _current$data5, _current$data5$childr;
      if (current !== null && current !== void 0 && (_current$data4 = current.data) !== null && _current$data4 !== void 0 && _current$data4.isGroupOpen && (current === null || current === void 0 ? void 0 : (_current$data5 = current.data) === null || _current$data5 === void 0 ? void 0 : (_current$data5$childr = _current$data5.children) === null || _current$data5$childr === void 0 ? void 0 : _current$data5$childr.length) > 0) {
        api.applyTransaction({
          remove: current.data.children
        });
        delete current.data.children;
        delete current.data.isGroupOpen;
        api.applyTransaction({
          update: [current.data]
        });
      }
    }
  });
}
function addCustomDrag(_ref12) {
  let {
    getCustomDragInfo,
    immutableRowData,
    rowData,
    dragPreview,
    previewKey,
    canDrop,
    onDragDrop,
    dragTarget,
    canDrag
  } = _ref12;
  const targetNode = document.querySelector('.ag-center-cols-container');
  const config = {
    childList: true
  };
  const rows = document.querySelectorAll('.w5-row');
  for (let row of rows) {
    addDragEvents({
      row,
      getCustomDragInfo,
      immutableRowData,
      rowData,
      dragPreview,
      previewKey,
      canDrop,
      onDragDrop,
      dragTarget,
      canDrag
    });
  }
  const callback = (mutationsList, observer) => {
    mutationsList.forEach(mutation => {
      if (mutation.type !== 'childList') return;
      mutation.addedNodes.forEach(node => {
        addDragEvents({
          row: node,
          getCustomDragInfo,
          immutableRowData,
          rowData,
          dragPreview,
          previewKey,
          canDrop,
          onDragDrop,
          dragTarget,
          canDrag
        });
      });
    });
  };
  const observer = new MutationObserver(callback);
  observer.observe(targetNode, config);
  return observer;
}
function createDefaultDragPreview(event, previewElement, data, previewKey) {
  const previewStyles = {
    width: '125px',
    height: '35px',
    padding: '0px 10px',
    borderRadius: '2px',
    backgroundColor: '#5a89fd',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: '"Sage Text"',
    fontSize: '13px'
  };
  _extends(previewElement.style, previewStyles);
  const previewText = document.createElement('div');
  const previewTextStyles = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'white'
  };
  _extends(previewElement.style, previewTextStyles);
  previewText.innerHTML = data.name || data[previewKey];
  previewElement.appendChild(previewText);
  document.body.appendChild(previewElement);
  event.dataTransfer.setDragImage(previewElement, 0, 0);
}
function addDragEvents(_ref13) {
  var _immutableRowData$cur2;
  let {
    row,
    rowData,
    getCustomDragInfo,
    immutableRowData,
    dragPreview,
    previewKey,
    canDrop,
    onDragDrop,
    dragTarget,
    canDrag
  } = _ref13;
  if (row.getAttribute('draggable')) return;
  const rowIndex = row.getAttribute('row-index');
  const data = ((_immutableRowData$cur2 = immutableRowData.current) === null || _immutableRowData$cur2 === void 0 ? void 0 : _immutableRowData$cur2[rowIndex]) || (rowData === null || rowData === void 0 ? void 0 : rowData[rowIndex]);
  if (canDrag && canDrag(data) || !canDrag) {
    row.setAttribute('draggable', true);
    row.addEventListener('dragstart', function (event) {
      var _immutableRowData$cur3;
      const previewElement = document.createElement('div');
      previewElement.setAttribute('id', 'w5-drag-preview');
      const rowId = row.getAttribute('row-id');
      const rowData = (_immutableRowData$cur3 = immutableRowData.current) === null || _immutableRowData$cur3 === void 0 ? void 0 : _immutableRowData$cur3[row.getAttribute('row-index')];
      event.dataTransfer.setData('text/plain', JSON.stringify(rowData));
      event.dataTransfer.setData('id', rowId);
      if (dragPreview) {
        const DragPreview = dragPreview;
        const preview = renderToStaticMarkup(/*#__PURE__*/React.createElement(DragPreview, {
          data: rowData,
          previewKey: previewKey
        }));
        previewElement.innerHTML = preview;
        document.body.appendChild(previewElement);
        event.dataTransfer.setDragImage(previewElement, 0, 0);
      } else {
        createDefaultDragPreview(event, previewElement, rowData, previewKey);
      }
      event.dataTransfer.dropEffect = 'move';
      getCustomDragInfo === null || getCustomDragInfo === void 0 ? void 0 : getCustomDragInfo({
        dragged: rowId
      });
    }, false);
  }
  row.addEventListener('dragend', function (event) {
    const previewElement = document.getElementById('w5-drag-preview');
    previewElement === null || previewElement === void 0 ? void 0 : previewElement.remove();
    row.classList.remove('w5-drag-over');
    getCustomDragInfo === null || getCustomDragInfo === void 0 ? void 0 : getCustomDragInfo({
      dragged: null
    });
  }, false);
  row.addEventListener('dragenter', function (event) {
    var _immutableRowData$cur4;
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget !== dragTarget.current) dragTarget.current = null;
    const rowData = (_immutableRowData$cur4 = immutableRowData.current) === null || _immutableRowData$cur4 === void 0 ? void 0 : _immutableRowData$cur4[row.getAttribute('row-index')];
    const allowDrop = canDrop ? canDrop(rowData) : true;
    if (allowDrop) {
      row.classList.add('w5-drag-over');
    }
  }, false);
  row.addEventListener('dragleave', function (event) {
    var _event$fromElement;
    event.preventDefault();
    event.stopPropagation();
    const inRow = (_event$fromElement = event.fromElement) === null || _event$fromElement === void 0 ? void 0 : _event$fromElement.closest('.ag-row');
    if (event.currentTarget === dragTarget.current && inRow) return;
    if (!inRow) {
      dragTarget.current = event.currentTarget;
    } else dragTarget.current = event.currentTarget;
    row.classList.remove('w5-drag-over');
    getCustomDragInfo === null || getCustomDragInfo === void 0 ? void 0 : getCustomDragInfo({
      target: null
    });
  }, false);
  row.addEventListener('drop', function (event) {
    var _event$target;
    const targetRow = event === null || event === void 0 ? void 0 : (_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.closest('.ag-row');
    targetRow === null || targetRow === void 0 ? void 0 : targetRow.classList.remove('w5-drag-over');
    try {
      var _immutableRowData$cur5;
      onDragDrop && onDragDrop(JSON.parse(event.dataTransfer.getData('text/plain')), (_immutableRowData$cur5 = immutableRowData.current) === null || _immutableRowData$cur5 === void 0 ? void 0 : _immutableRowData$cur5[targetRow.getAttribute('row-index')]);
    } catch (e) {
      console.error(e);
    }
  }, false);
  row.addEventListener('dragover', function (event) {
    var _immutableRowData$cur6;
    const rowId = row.getAttribute('row-id');
    const rowData = (_immutableRowData$cur6 = immutableRowData.current) === null || _immutableRowData$cur6 === void 0 ? void 0 : _immutableRowData$cur6[row.getAttribute('row-index')];
    const allowDrop = canDrop ? canDrop(rowData) : true;
    if (allowDrop) {
      event.preventDefault();
      getCustomDragInfo === null || getCustomDragInfo === void 0 ? void 0 : getCustomDragInfo({
        target: rowId
      });
    }
    return false;
  }, false);
}